import { CoreInterfaces, DTOs } from "./../../Models";
import * as Constants from "../../Constants/Constants";
import {
  buildNextState,
  createEmptyContactPerson,
  createEmptyLicense,
  findQuestionInService,
  isContactPersonDataFilled,
  isLicenseDataFilled,
  applyChangesTimestampOnNextState,
  checkContactPersonUserDetailsFilled,
  createEmptyClient,
  isClientDetailsDataFilled,
  createEmptySignee,
} from "src/utils";

export function contactsReducer(
  state: CoreInterfaces.AppState,
  action: { type: Constants.AppStateActions; payload?: any }
): Partial<CoreInterfaces.AppState> {
  switch (action.type) {
    case Constants.AppStateActions.UpdateLicenseValue: {
      const { question, licenseUuid, property, value } = action.payload;

      const stateService = state.services.find(
        (s) =>
          s.data.code === Constants.ServiceCode.OtherAccountAndReconciliation
      );
      const stateQuestion = stateService.data.questions.find(
        (q) => q.data.code === question.data.code
      );
      const emptyLicense = createEmptyLicense();
      emptyLicense.Uuid = licenseUuid;
      const licenses = !!stateQuestion.data.userValue
        ? (stateQuestion.data.userValue as unknown as CoreInterfaces.License[])
        : [emptyLicense];
      if (Array.isArray(licenses)) {
        const stateLicense = licenses.find((l) => l.Uuid === licenseUuid);
        if (!!stateLicense) {
          const nextStateLicense = { ...stateLicense, [property]: value };
          const nextStateQuestion = {
            ...stateQuestion,
            data: {
              ...stateQuestion.data,
              userValue: licenses.map((l) => {
                if (l.Uuid === licenseUuid) {
                  return nextStateLicense;
                }
                return l;
              }),
            },
            state: {
              ...stateQuestion.state,
              isFilled:
                stateQuestion.state.isFilled &&
                isLicenseDataFilled(stateQuestion),
            },
          };

          return buildNextState(
            state,
            nextStateQuestion,
            Constants.ServiceCode.OtherAccountAndReconciliation
          );
        } else {
          return state;
        }
      }
      return state;
    }
    case Constants.AppStateActions.AddNewLicense: {
      const stateService = state.services.find(
        (s) =>
          s.data.code === Constants.ServiceCode.OtherAccountAndReconciliation
      );
      const stateQuestion = stateService.data.questions.find(
        (q) =>
          q.data.code === Constants.OtherAccountAndReconciliationQuestions.Q0411
      );
      const nextStateQuestion = {
        ...stateQuestion,
        data: {
          ...stateQuestion.data,
          userValue: [
            ...(Array.isArray(stateQuestion.data.userValue)
              ? stateQuestion.data.userValue
              : []),
            createEmptyLicense(),
          ] as CoreInterfaces.License[],
        },
        state: {
          ...stateQuestion.state,
          isFilled: false,
        },
      };

      return buildNextState(
        state,
        nextStateQuestion,
        Constants.ServiceCode.OtherAccountAndReconciliation
      );
    }
    case Constants.AppStateActions.DeleteLicense: {
      const { licenseToDeleteUuid } = action.payload;
      const stateService = state.services.find(
        (s) =>
          s.data.code === Constants.ServiceCode.OtherAccountAndReconciliation
      );
      const stateQuestion = findQuestionInService(
        stateService,
        Constants.OtherAccountAndReconciliationQuestions.Q0411
      );
      const nextLicenses = (
        (stateQuestion.data.userValue as CoreInterfaces.License[]) ?? []
      ).filter((l) => l.Uuid !== licenseToDeleteUuid);
      const nextStateQuestion = {
        ...stateQuestion,
        data: {
          ...stateQuestion.data,
          userValue: nextLicenses,
        },
      };
      return buildNextState(
        state,
        nextStateQuestion,
        Constants.ServiceCode.OtherAccountAndReconciliation
      );
    }

    case Constants.AppStateActions.UpdateContactPersonValue:
    case Constants.AppStateActions.AddContactPerson:
    case Constants.AppStateActions.DeleteContactPerson: {
      const serviceToUpdate = Constants.ServiceCode.GeneralInformation;
      const stateService = state.services.find(
        (s) => s.data.code === serviceToUpdate
      );
      const stateQuestion = findQuestionInService(
        stateService,
        Constants.GeneralInformationQuestion.Q0034
      );
      let contactPersons: CoreInterfaces.ContactPersonUserDetails[] = [
        createEmptyContactPerson(true),
      ];
      if (Array.isArray(stateQuestion.data.userValue)) {
        contactPersons = stateQuestion.data
          .userValue as CoreInterfaces.ContactPersonUserDetails[];
      }
      let updatedContactPersons;
      if (action.type === Constants.AppStateActions.UpdateContactPersonValue) {
        const { contactPersonUuid, property, value } = action.payload;
        if (contactPersons.length === 0) {
          contactPersons.push(createEmptyContactPerson(true));
        }
        updatedContactPersons = contactPersons.map((contactPerson) => {
          if (contactPerson.Uuid === contactPersonUuid) {
            return {
              ...contactPerson,
              [property]: value,
            };
          }
          return contactPerson;
        });
      } else if (action.type === Constants.AppStateActions.AddContactPerson) {
        updatedContactPersons = [
          ...contactPersons,
          createEmptyContactPerson(false),
        ];
      } else if (
        action.type === Constants.AppStateActions.DeleteContactPerson
      ) {
        const { contactPersonToDeleteUuid } = action.payload;
        updatedContactPersons = contactPersons.filter(
          (contactPerson) => contactPerson.Uuid !== contactPersonToDeleteUuid
        );
        if (updatedContactPersons.length === 1) {
          updatedContactPersons[0].IsSigned = true;
        }
      }

      const nextStateQuestion = {
        ...stateQuestion,
        data: {
          ...stateQuestion.data,
          userValue: updatedContactPersons as unknown as string,
        },
        state: {
          ...stateQuestion.state,
          isFilled:
            action.type !== Constants.AppStateActions.DeleteContactPerson &&
            stateQuestion.state.isFilled &&
            isContactPersonDataFilled(stateQuestion) &&
            checkContactPersonUserDetailsFilled(stateQuestion),
        },
      };

      const nextState = buildNextState(
        state,
        nextStateQuestion,
        serviceToUpdate
      );
      applyChangesTimestampOnNextState(nextState);
      return nextState;
    }
    case Constants.AppStateActions.UpdateClientDetailsValue:
    case Constants.AppStateActions.AddClientDetails:
    case Constants.AppStateActions.DeleteClientDetails: {
      const serviceToUpdate = Constants.ServiceCode.GeneralInformation;
      const stateService = state.services.find(
        (s) => s.data.code === serviceToUpdate
      );
      const stateQuestion = findQuestionInService(
        stateService,
        Constants.GeneralInformationQuestion.QT145
      );
      let contactPersons: CoreInterfaces.ClientDetails[] = [
        createEmptyClient(state, true),
      ];
      if (Array.isArray(stateQuestion.data.userValue)) {
        contactPersons = stateQuestion.data
          .userValue as CoreInterfaces.ClientDetails[];
      }
      let updatedContactPersons;
      if (action.type === Constants.AppStateActions.UpdateClientDetailsValue) {
        const { contactPersonUuid, property, value } = action.payload;
        if (contactPersons.length === 0) {
          contactPersons.push(createEmptyClient(state, true));
        }
        updatedContactPersons = contactPersons.map((contactPerson) => {
          if (
            contactPersons.length === 1 ||
            contactPerson.Uuid === contactPersonUuid
          ) {
            return {
              ...contactPerson,
              [property]: value,
            };
          }
          if ("ClientType" === property) {
            if (contactPerson.Uuid === contactPersonUuid) {
              if (value === Constants.ClientType.PrivatePerson) {
                contactPerson.Signees = [
                  {
                    FirstName: "",
                    LastName: "",
                    Email: "",
                    SigningMethod: Constants.SigningMethod.BankIDSE,
                  },
                ];
              }
              if (value === Constants.ClientType.Company) {
                contactPerson.GTSignee = {
                  FirstName: "",
                  LastName: "",
                  Email: "",
                  SigningMethod: Constants.SigningMethod.BankIDSE,
                };
              }
            }
          }
          return contactPerson;
        });
      } else if (action.type === Constants.AppStateActions.AddClientDetails) {
        updatedContactPersons = [
          ...contactPersons,
          createEmptyClient(state, false),
        ];
      } else if (
        action.type === Constants.AppStateActions.DeleteClientDetails
      ) {
        const { contactPersonToDeleteUuid } = action.payload;
        updatedContactPersons = contactPersons.filter(
          (contactPerson) => contactPerson.Uuid !== contactPersonToDeleteUuid
        );
      }

      const nextStateQuestion = {
        ...stateQuestion,
        data: {
          ...stateQuestion.data,
          userValue: updatedContactPersons as unknown as string,
        },
        state: {
          ...stateQuestion.state,
          isFilled:
            action.type !== Constants.AppStateActions.DeleteClientDetails &&
            stateQuestion.state.isFilled &&
            isClientDetailsDataFilled(stateQuestion),
        },
      };
      applyChangesTimestampOnNextState(state);

      return buildNextState(state, nextStateQuestion, serviceToUpdate);
    }
    case Constants.AppStateActions.DeleteSignee: {
      const { contactPersonUuid, signeeIndex } = action.payload;
      const serviceToUpdate = Constants.ServiceCode.GeneralInformation;
      const stateService = state.services.find(
        (s) => s.data.code === serviceToUpdate
      );
      const stateQuestion = findQuestionInService(
        stateService,
        Constants.GeneralInformationQuestion.QT145
      );

      const contactPersons: CoreInterfaces.ClientDetails[] = Array.isArray(
        stateQuestion.data.userValue
      )
        ? (stateQuestion.data.userValue as CoreInterfaces.ClientDetails[])
        : [createEmptyClient(state, true)];

      const updatedContactPersons = contactPersons.map((client) => {
        if (client.Uuid === contactPersonUuid) {
          return {
            ...client,
            Signees: client.Signees.filter((_, index) => index !== signeeIndex),
          };
        }
        return client;
      });

      const nextStateQuestion = {
        ...stateQuestion,
        data: {
          ...stateQuestion.data,
          userValue: updatedContactPersons as unknown as string,
        },
      };

      return buildNextState(state, nextStateQuestion, serviceToUpdate);
    }
    case Constants.AppStateActions.AddSigneeDetails:
    case Constants.AppStateActions.DeleteSigneeDetails:
    case Constants.AppStateActions.UpdateSigneeDetails: {
      const { questionCode } = action.payload as {
        questionCode: keyof typeof Constants.GeneralInformationQuestion;
      };
      const serviceToUpdate = Constants.ServiceCode.GeneralInformation;
      const stateService = state.services.find(
        (s) => s.data.code === serviceToUpdate
      );
      const stateQuestion = findQuestionInService(
        stateService,
        Constants.GeneralInformationQuestion[questionCode]
      );
      let signees: CoreInterfaces.ClientSigneeDetails[] = [createEmptySignee()];
      if (Array.isArray(stateQuestion.data.userValue)) {
        signees = stateQuestion.data
          .userValue as CoreInterfaces.ClientSigneeDetails[];
      }
      let updatedContactPersons;
      if (action.type === Constants.AppStateActions.UpdateSigneeDetails) {
        const { clientSigneeUuid, property, value } = action.payload;
        if (signees.length === 0) {
          signees.push(createEmptySignee());
        }
        updatedContactPersons = signees.map((signee) => {
          if (signees.length === 1 || signee.Uuid === clientSigneeUuid) {
            return {
              ...signee,
              [property]: value,
            };
          }

          return signee;
        });
      } else if (action.type === Constants.AppStateActions.AddSigneeDetails) {
        updatedContactPersons = [...signees, createEmptySignee()];
      } else if (
        action.type === Constants.AppStateActions.DeleteSigneeDetails
      ) {
        const { contactPersonToDeleteUuid } = action.payload;
        updatedContactPersons = signees.filter(
          (contactPerson) => contactPerson.Uuid !== contactPersonToDeleteUuid
        );
      }

      const nextStateQuestion = {
        ...stateQuestion,
        data: {
          ...stateQuestion.data,
          userValue: updatedContactPersons as unknown as string,
        },
        state: {
          ...stateQuestion.state,
          isFilled:
            action.type !== Constants.AppStateActions.DeleteSigneeDetails &&
            stateQuestion.state.isFilled &&
            isClientDetailsDataFilled(stateQuestion),
        },
      };
      applyChangesTimestampOnNextState(state);

      return buildNextState(state, nextStateQuestion, serviceToUpdate);
    }
    default:
      return {};
  }
}
